import React, { useState, useEffect, useCallback } from 'react';
import type { Tentor } from '../../types/db';
import { getLastCutoff, runPayrollGeneration } from '../../services/financeService';
import { supabase } from '../../lib/supabaseClient';
import {
  Calculator,
  Loader2,
  Sparkles,
  ShieldCheck,
  History,
  Lock,
  RefreshCw,
  Save,
  Layers,
} from 'lucide-react';

export function PayrollManager({ tentors, branchId }: { tentors: Tentor[]; branchId: string }) {
  const [activeMode, setActiveMode] = useState<'PREVIEW' | 'GENERATE'>('PREVIEW');
  const [loading, setLoading] = useState(false);
  const [lastCutoff, setLastCutoff] = useState<string | null>(null);

  // Preview States
  const [selectedTentorId, setSelectedTentorId] = useState('');
  const [previewToDate, setPreviewToDate] = useState(new Date().toISOString().split('T')[0]);
  const [previewResult, setPreviewResult] = useState<{
    uniqueSessions: any[];
    totalHonor: number;
  } | null>(null);

  // Generate States
  const [genPeriod, setGenPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [genCutoffDate, setGenCutoffDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchMetadata = useCallback(async () => {
    setLoading(true);
    try {
      const date = await getLastCutoff(branchId);
      setLastCutoff(date);
    } catch (e: any) {
      /* handled silently */
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  // MODE 1: Preview Real-time (Sinkron dengan batch_group_id & time slot)
  const handlePreview = async () => {
    if (!selectedTentorId || !previewToDate) return;
    setLoading(true);
    try {
      const tentor = tentors.find(
        (t) => t.id === selectedTentorId || t.user_id === selectedTentorId
      );
      if (!tentor) throw new Error('Tentor tidak ditemukan');

      const startDate = lastCutoff
        ? new Date(new Date(lastCutoff).getTime() + 86400000).toISOString().split('T')[0]
        : previewToDate.slice(0, 8) + '01';

      const { data: sessions, error } = await supabase
        .from('schedules')
        .select('*, class_room:classes(name)')
        .eq('teacher_user_id', tentor.user_id)
        .eq('branch_id', branchId)
        .gte('date', startDate)
        .lte('date', previewToDate)
        .order('date', { ascending: true });

      if (error) throw error;

      // ✅ LOGIKA GROUPING PAYROLL: Sinkron dengan batch_group_id dari ScheduleManager
      const sessionGroups: Record<string, any> = {};
      (sessions || []).forEach((s) => {
        // Prioritaskan grouping via batch_group_id, fallback ke gabungan tanggal_jam jika data lama
        const groupKey = s.batch_group_id || `${s.date}_${s.time}_${s.subject}`;
        
        if (!sessionGroups[groupKey]) {
          sessionGroups[groupKey] = {
            ...s,
            merged_classes: [s.class_room?.name || s.grade],
          };
        } else {
          const className = s.class_room?.name || s.grade;
          if (!sessionGroups[groupKey].merged_classes.includes(className)) {
            sessionGroups[groupKey].merged_classes.push(className);
          }
        }
      });

      const uniqueSessions = Object.values(sessionGroups);
      const totalHonor = uniqueSessions.length * (tentor.base_salary || 0);

      setPreviewResult({ uniqueSessions, totalHonor });
    } catch (e: any) {
      window.alert('Gagal Preview: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!genPeriod || !genCutoffDate) return;

    if (lastCutoff && new Date(genCutoffDate) <= new Date(lastCutoff)) {
      return window.alert(
        `EROR: Tanggal cut-off baru (${genCutoffDate}) harus setelah cut-off terakhir (${lastCutoff})`
      );
    }

    if (
      !window.confirm(
        `KONFIRMASI PAYROLL:\nSesi Gabungan (Paralel) akan otomatis dihitung sebagai 1 sesi honor per waktu mengajar.\nLanjutkan proses kunci data?`
      )
    )
      return;

    setLoading(true);
    try {
      await runPayrollGeneration(branchId, genPeriod, genCutoffDate);
      window.alert(
        'BERHASIL! Honor telah dikunci dan data sesi gabungan dihitung sebagai satu sesi tunggal.'
      );
      fetchMetadata();
      setActiveMode('PREVIEW');
    } catch (e: any) {
      window.alert('Gagal Generate: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in font-sans">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-slate-900 p-8 rounded-[3rem] text-white shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
        <div className="relative z-10 flex items-center gap-6">
          <div className="w-16 h-16 bg-white/10 rounded-[2rem] flex items-center justify-center backdrop-blur-md border border-white/10">
            <Calculator className="w-8 h-8 text-indigo-300" />
          </div>
          <div>
            <h3 className="text-2xl font-black uppercase tracking-tight leading-none">
              Smart Payroll Engine
            </h3>
            <p className="text-[9px] text-indigo-300 font-bold uppercase tracking-[0.3em] mt-2 flex items-center gap-2">
              <ShieldCheck className="w-3 h-3" /> Akurasi Perhitungan Sesi Gabungan
            </p>
          </div>
        </div>

        <div className="relative z-10 flex bg-white/5 p-1 rounded-2xl backdrop-blur-sm border border-white/10">
          <button
            onClick={() => setActiveMode('PREVIEW')}
            className={`px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${activeMode === 'PREVIEW' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
          >
            Cek Honor
          </button>
          <button
            onClick={() => setActiveMode('GENERATE')}
            className={`px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${activeMode === 'GENERATE' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
          >
            Tutup Periode
          </button>
        </div>
      </div>

      <div className="bg-indigo-50 border border-indigo-100 p-6 rounded-[2rem] flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-indigo-600 shadow-sm">
            <History className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">
              Cut-off Terakhir
            </p>
            <p className="text-lg font-black text-indigo-900">
              {lastCutoff
                ? new Date(lastCutoff).toLocaleDateString('id-ID', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })
                : 'BELUM ADA DATA'}
            </p>
          </div>
        </div>
        <button
          onClick={fetchMetadata}
          className="p-3 text-indigo-400 hover:text-indigo-600 hover:bg-white rounded-xl transition-all"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
          {activeMode === 'PREVIEW' ? (
            <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm space-y-6 animate-fade-in">
              <h4 className="font-black text-slate-800 uppercase text-xs tracking-widest border-b pb-4">
                Estimasi Honor
              </h4>
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-indigo-600 uppercase ml-2">
                    Pilih Tentor
                  </label>
                  <select
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black bg-slate-50/50 outline-none text-xs"
                    value={selectedTentorId}
                    onChange={(e) => {
                      setSelectedTentorId(e.target.value);
                      setPreviewResult(null);
                    }}
                  >
                    <option value="">-- CARI NAMA --</option>
                    {tentors.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name?.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-indigo-600 uppercase ml-2">
                    Sampai Tanggal
                  </label>
                  <input
                    type="date"
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black bg-slate-50/50 outline-none text-xs"
                    value={previewToDate}
                    onChange={(e) => {
                      setPreviewToDate(e.target.value);
                      setPreviewResult(null);
                    }}
                  />
                </div>
                <button
                  onClick={handlePreview}
                  disabled={loading || !selectedTentorId}
                  className="w-full bg-slate-900 text-white py-5 rounded-[2rem] font-black uppercase text-[10px] tracking-widest shadow-xl hover:bg-indigo-600 transition-all flex justify-center items-center gap-3"
                >
                  {loading ? (
                    <Loader2 className="animate-spin w-4 h-4" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}{' '}
                  HITUNG ESTIMASI
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm space-y-6 animate-fade-in">
              <div className="flex items-center gap-3 border-b pb-4">
                <Lock className="text-rose-500 w-4 h-4" />
                <h4 className="font-black text-rose-800 uppercase text-xs tracking-widest">
                  Finalisasi Honor
                </h4>
              </div>
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-indigo-600 uppercase ml-2">
                    Periode (Bulan)
                  </label>
                  <input
                    type="month"
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black bg-slate-50/50 outline-none text-xs"
                    value={genPeriod}
                    onChange={(e) => setGenPeriod(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-indigo-600 uppercase ml-2">
                    Tanggal Cut-off Baru
                  </label>
                  <input
                    type="date"
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black bg-slate-50/50 outline-none text-xs"
                    value={genCutoffDate}
                    onChange={(e) => setGenCutoffDate(e.target.value)}
                  />
                </div>
                <button
                  onClick={handleGenerate}
                  disabled={
                    !!(loading || (lastCutoff && new Date(genCutoffDate) <= new Date(lastCutoff)))
                  }
                  className="w-full bg-rose-600 text-white py-5 rounded-[2rem] font-black uppercase text-[10px] tracking-widest shadow-xl hover:bg-rose-700 transition-all flex justify-center items-center gap-3 disabled:opacity-30"
                >
                  {loading ? (
                    <Loader2 className="animate-spin w-4 h-4" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}{' '}
                  GENERATE & KUNCI HONOR
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          {previewResult ? (
            <div className="space-y-6 animate-fade-in-up">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
                    Total Sesi Mengajar
                  </p>
                  <p className="text-4xl font-black text-slate-900">
                    {previewResult.uniqueSessions.length}
                  </p>
                </div>
                <div className="bg-indigo-600 p-8 rounded-[2.5rem] text-white shadow-xl shadow-indigo-100">
                  <p className="text-[9px] font-black text-indigo-200 uppercase tracking-widest mb-1">
                    Total Honor Terakumulasi
                  </p>
                  <p className="text-3xl font-black">
                    Rp {previewResult.totalHonor.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-[2.5rem] border border-slate-100 overflow-hidden shadow-sm">
                <div className="p-6 border-b bg-slate-50 flex items-center justify-between">
                  <h5 className="font-black text-[10px] uppercase text-slate-500 tracking-widest">
                    Daftar Sesi (Gabungan Dihitung 1)
                  </h5>
                </div>
                <div className="max-h-[400px] overflow-y-auto no-scrollbar">
                  <table className="w-full text-[11px]">
                    <tbody className="divide-y divide-slate-50 font-bold">
                      {previewResult.uniqueSessions.map((s, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                          <td className="p-5 w-10 text-slate-300">#{idx + 1}</td>
                          <td className="p-5 font-mono text-slate-400">
                            {s.date} | {s.time}
                          </td>
                          <td className="p-5 text-slate-700 uppercase">{s.subject}</td>
                          <td className="p-5 text-right">
                            <div className="flex flex-wrap gap-1 justify-end">
                              {s.merged_classes.map((c: string, i: number) => (
                                <span
                                  key={i}
                                  className="text-[7px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded uppercase flex items-center gap-1"
                                >
                                  <Layers className="w-2 h-2" /> {c}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full border-4 border-dashed border-slate-50 rounded-[4rem] flex flex-col items-center justify-center p-20 text-center text-slate-200 bg-white/50">
              <Calculator className="w-20 h-20 mb-6 opacity-5" />
              <p className="font-black uppercase tracking-[0.4em] text-xs">
                Pilih Tentor Untuk Cek Perhitungan Sesi
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
